import React from 'react';
import { View, Text, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import { IColumnConfig } from '@/types/admin/dataTable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface DesktopFilterRowProps<T> {
  index: number;
  filter: { field: string; operator: string; value: any };
  columnByKey: Record<string, IColumnConfig<T>>;
  filterableColumns: IColumnConfig<T>[];
  operatorOptions: Array<{ label: string; value: string }>;
  onChangeField: (index: number, fieldKey: string) => void;
  onChangeOperator: (index: number, operator: string) => void;
  onChangeValue: (index: number, value: any) => void;
  onRemove: (index: number) => void;
  onOpenColumnPicker: (index: number) => void;
  onOpenOperatorPicker: (index: number) => void;
  onOpenValuePicker: (index: number) => void;
}

export const DesktopFilterRow = <T,>({
  index,
  filter,
  columnByKey,
  operatorOptions,
  onChangeValue,
  onRemove,
  onOpenColumnPicker,
  onOpenOperatorPicker,
  onOpenValuePicker,
}: DesktopFilterRowProps<T>) => {
  const styles = useStyles();
  const { admin, redesign } = useAdminColors();
  const currentColumn = columnByKey[filter.field];

  return (
    <View style={styles.filterRow}>
      <View style={styles.filterField}>
        <Text style={styles.filterLabel}>Column</Text>
        <TouchableOpacity
          style={[styles.filterInput, styles.filterInputRow]}
          onPress={() => onOpenColumnPicker(index)}
        >
          <Text style={styles.filterValueText}>{currentColumn?.label || filter.field}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.filterField}>
        <Text style={styles.filterLabel}>Operator</Text>
        <TouchableOpacity
          style={[styles.filterInput, styles.filterInputRow]}
          onPress={() => onOpenOperatorPicker(index)}
        >
          <Text style={styles.filterValueText}>
            {operatorOptions.find((o) => o.value === filter.operator)?.label || filter.operator}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.filterField}>
        <Text style={styles.filterLabel}>Value</Text>
        {currentColumn?.filterType === 'date' ? (
          <TextInput
            style={[styles.filterInput, styles.filterValueText]}
            value={filter.value}
            onChangeText={(value) => onChangeValue(index, value)}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={admin.formInput.placeholder}
          />
        ) : currentColumn?.filterType === 'select' ||
          currentColumn?.filterType === 'multiSelect' ||
          currentColumn?.filterType === 'boolean' ? (
          <TouchableOpacity
            style={[styles.filterInput, styles.filterInputRow]}
            onPress={() => onOpenValuePicker(index)}
          >
            <Text style={styles.filterValueText}>
              {String(filter.value ?? '') || 'Select value'}
            </Text>
          </TouchableOpacity>
        ) : (
          <TextInput
            style={[styles.filterInput, styles.filterValueText]}
            value={filter.value}
            onChangeText={(value) => onChangeValue(index, value)}
            placeholder="Enter value"
            placeholderTextColor={admin.formInput.placeholder}
          />
        )}
      </View>

      <TouchableOpacity onPress={() => onRemove(index)} style={styles.removeFilterButton}>
        <SVG_COMPONENTS.CANCEL_X width={16} height={16} color={redesign.text.dim} />
      </TouchableOpacity>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      filterRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 12, marginBottom: 12, width: '100%' },
      filterField: { flex: 1, position: 'relative' },
      filterLabel: { fontSize: 12, marginBottom: 4, color: admin.formInput.label },
      filterInput: {
        borderWidth: 1,
        borderColor: admin.formInput.border,
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 6,
        fontSize: 14,
        backgroundColor: admin.formInput.bg,
      },
      filterInputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      filterValueText: {
        color: admin.formInput.text,
        fontSize: 14,
      },
      removeFilterButton: { padding: 8, marginBottom: 2 },
    });
  });
